import type { MouseEvent } from 'react';
import type { DataGridColumnProps } from '../DataGrid/DataGrid';
import type { ResultsPerPageChangeFn } from '../Pagination/ResultsPerPageDropdown';
import type { DataGridColumnsVisibilityChangeFn } from './ColumnsVisibility/ColumnsVisibility';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import { RefreshIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import ResultsPerPageDropdown from '../Pagination/ResultsPerPageDropdown';
import ColumnsVisibility from './ColumnsVisibility/ColumnsVisibility';

export interface DataGridColumnOptionsProps {
  colId: string;
  hide?: boolean;
  width?: number;
}

export interface DataGridControlProps {
  /**
   * Display Edit Grid and Filter Data checkboxes
   */
  displayGridCheckboxes?: boolean;
  /**
   * Display grid controls
   */
  displayGridControl?: boolean;
  /**
   * Reset grid callback
   */
  handleResetGrid: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Is grid edited
   */
  editGrid?: boolean;
  /**
   * Set is grid edited
   */
  setEditGrid: (value: boolean) => void;
  /**
   * Filter grid
   */
  filterGrid?: boolean;
  /**
   * Set filter grid
   */
  setFilterGrid: (value: boolean) => void;
  /**
   * Rows limit
   */
  limit?: number;
  /**
   * Handle limit rows
   */
  handleLimitChange: ResultsPerPageChangeFn;
  /**
   * Grid options
   */
  gridOptions?: DataGridColumnOptionsProps[];
  /**
   * Grid columns
   */
  columns?: DataGridColumnProps[];
  /**
   * Handle visible column changes
   */
  optionsLocalStorageKey: string;
  /**
   * Callback to update grid options value on visibility change
   */
  setGridOptions: (options: DataGridColumnOptionsProps[]) => void;
  /**
   * Additional classes
   */
  columnsVisibilityAdditionalClasses?: string;
  /**
   *  If rows are being deleted
   */
  isDeleting?: boolean;
  /**
   * Test id
   */
  testId?: string;
}

const DataGridControl = ({
  displayGridCheckboxes = false,
  displayGridControl = false,
  handleResetGrid = EMPTY_FUNCTION,
  editGrid = false,
  setEditGrid = EMPTY_FUNCTION,
  filterGrid = false,
  limit = 20,
  setFilterGrid,
  handleLimitChange,
  columns = EMPTY_ARRAY,
  optionsLocalStorageKey,
  columnsVisibilityAdditionalClasses = '',
  isDeleting = false,
  testId = '',
}: DataGridControlProps) => {
  const { t } = useTranslation();

  const [gridOptions, setGridOptions] = useLocalStorageState<
    DataGridColumnOptionsProps[]
  >(optionsLocalStorageKey);

  const handleVisibleColumnsChange =
    useCallback<DataGridColumnsVisibilityChangeFn>(
      (options) => {
        setGridOptions(options);
      },
      [setGridOptions],
    );

  return (
    <>
      {displayGridCheckboxes && (
        <div
          className={twMerge(
            'inline-flex items-center w-full xs:w-auto',
            isDeleting && 'cursor-not-allowed opacity-50',
          )}
        >
          <Checkbox
            name="edit"
            checked={editGrid}
            onChange={() => setEditGrid(!editGrid)}
            label={t('ObjectsOfType.EditGrid')}
            additionalLabelClasses={twMerge(
              'ml-2 text-sm md:text-base mr-2',
              isDeleting && 'opacity-50',
            )}
            additionalCheckboxClasses="mt-0 lg:mt-0.5 h-4 md:h-5 w-4 md:w-5
                bg-size-[8px_8px] md:bg-size-[10px_10px] bg-white border-slate-300 checked:border-blue"
            disabled={isDeleting}
          />

          <Checkbox
            name="filter"
            checked={filterGrid}
            onChange={() => setFilterGrid(!filterGrid)}
            label={t('ObjectsOfType.FilterGrid')}
            additionalLabelClasses="ml-2 mr-2 text-sm md:text-base"
            additionalCheckboxClasses="mt-0 lg:mt-0.5 h-4 md:h-5 w-4 md:w-5
                bg-size-[8px_8px] md:bg-size-[10px_10px] bg-white border-slate-300 checked:border-blue"
            {...getTestProps(testId, `checkbox-filter-data`, 'testId')}
          />
        </div>
      )}

      {displayGridControl && (
        <div
          className={twMerge(
            'flex items-center justify-start sm:justify-end w-full sm:w-auto gap-5',
            isDeleting && 'cursor-not-allowed opacity-50',
            editGrid && 'invisible xl:visible',
          )}
        >
          <Button
            iconImage={<RefreshIcon className="text-blue w-3 md:w-4" />}
            onClick={handleResetGrid}
            buttonColor="borderless"
            additionalClasses={twMerge(
              'text-sm md:text-base',
              isDeleting &&
                'bg-transparent hover:bg-transparent active:bg-transparent opacity-50',
            )}
            testId={'button-grid-reset'}
            disabled={isDeleting}
          >
            {t('Global.ResetGrid')}
          </Button>
          <ResultsPerPageDropdown
            value={limit}
            setResultsPerPage={handleLimitChange}
            additionalButtonClasses="pr-8! 2xl:py-1!"
            additionalClasses="p-0! text-sm md:text-base"
            label={t('Global.ResultsPerPage')}
            disabled={isDeleting}
            {...getTestProps(testId, 'results-per-page-dropdown', 'testId')}
          />
        </div>
      )}

      {editGrid && (
        <ColumnsVisibility
          columns={columns}
          testId={testId}
          gridOptions={gridOptions}
          onChange={handleVisibleColumnsChange}
          additionalClasses={twMerge(
            'sm:pl-2 rounded-lg bg-transparent! w-auto',
            'bottom-1.5 sm:bottom-auto sm:top-1.5 md:top-1 left:0 sm:left-auto xl:left-[300px]',
            'sm:right-3 xl:right-auto',
            columnsVisibilityAdditionalClasses,
          )}
        />
      )}
    </>
  );
};

export default DataGridControl;
